// utils/cartonItemFilters.js
//
// Which filter chips the carton page shows over a carton's lines, in what order,
// and which one it opens on (carton «نواقص» tab, owner-approved 2026-09-21).
//
// THE RULE, in one sentence: while the carton can still take an out-of-stock
// line back, and has at least one, the carton opens on «نواقص» and that chip
// comes first. Otherwise it opens on «الكل», and «نواقص» sits last, or is gone
// when there is nothing in it.
//
// "Can still take it back" is `boxCanTakeItBack()` — the SAME window the
// suggest door and the out-of-stock cancel read (ACTIVE or READY, not
// delivery-confirmed). Outside it the club has stopped looking and she can
// neither suggest nor cancel, so promoting the tab there would open her carton
// on a list she can do nothing with, under a hint that is no longer true.
//
// Pure function module — no Vue, no F7, no i18n.

import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { boxCanTakeItBack } from '@/utils/supplierSuggestion.js';

export const ItemFilter = Object.freeze({
  ALL: 'all',
  PROGRESS: 'progress',
  DONE: 'done',
  ISSUES: 'issues',
  CANCELLED: 'cancelled',
  OUT_OF_STOCK: 'outOfStock',
});

const BASE_ORDER = Object.freeze([
  ItemFilter.ALL,
  ItemFilter.PROGRESS,
  ItemFilter.DONE,
  ItemFilter.ISSUES,
  ItemFilter.CANCELLED,
]);

/** Coerced the way useItemStatus coerces: a `"41"` off the wire is 41. */
function statusOf(item) {
  const raw = item?.status;
  return typeof raw === 'string' ? Number(raw) : raw;
}

const FILTER_STATUSES = Object.freeze({
  [ItemFilter.PROGRESS]: [ItemStatus.PENDING, ItemStatus.COLLECTING],
  [ItemFilter.DONE]: [ItemStatus.COLLECTED, ItemStatus.PACKAGED],
  [ItemFilter.ISSUES]: [ItemStatus.ISSUE],
  [ItemFilter.CANCELLED]: [ItemStatus.CANCELLED],
  [ItemFilter.OUT_OF_STOCK]: [ItemStatus.OUT_OF_STOCK],
});

/**
 * Does this line belong under this chip? The ONE definition the chip's count,
 * the list under it and the plan below all read, so the three cannot disagree
 * — including about a status that arrived as a string.
 *
 * «الكل» is every line except a cancelled one. An unknown filter matches
 * nothing.
 *
 * @param {object} item
 * @param {string} filter one of ItemFilter
 * @returns {boolean}
 */
export function matchesItemFilter(item, filter) {
  const status = statusOf(item);
  if (filter === ItemFilter.ALL) return status !== ItemStatus.CANCELLED;
  return FILTER_STATUSES[filter]?.includes(status) ?? false;
}

/**
 * @param {Array<object>|null|undefined} items the carton's lines
 * @param {object|null|undefined} box the carton (status + deliveredConfirmedAt)
 * @returns {{ oosCount: number, oosLive: boolean, filters: string[], defaultFilter: string }}
 */
export function cartonFilterPlan(items, box) {
  const oosCount = (items ?? []).filter(
    (item) => matchesItemFilter(item, ItemFilter.OUT_OF_STOCK),
  ).length;
  const oosLive = oosCount > 0 && boxCanTakeItBack(box);

  let filters;
  if (oosLive) {
    filters = [ItemFilter.OUT_OF_STOCK, ...BASE_ORDER];
  } else if (oosCount > 0) {
    // A carton that has moved on with old out-of-stock lines in it (a legacy
    // one, from before asking to ship cancelled them): still findable, last.
    filters = [...BASE_ORDER, ItemFilter.OUT_OF_STOCK];
  } else {
    filters = [...BASE_ORDER];
  }

  return {
    oosCount,
    oosLive,
    filters,
    defaultFilter: oosLive ? ItemFilter.OUT_OF_STOCK : ItemFilter.ALL,
  };
}

/**
 * The filter actually in force.
 *
 * `chosen` is what she TAPPED, or null while she has not tapped anything on
 * this carton — the page resets it to null on every carton swipe and every
 * open/closed switch, so each carton decides for itself. Null follows the
 * carton's default, reactively: when the lines arrive, or when she cancels the
 * last missing item, the view follows without a watcher.
 *
 * A chosen «نواقص» whose chip has gone (she cancelled the last one) falls back
 * to «الكل» rather than leaving her on an empty list under a chip that is no
 * longer drawn.
 *
 * @param {string|null} chosen
 * @param {ReturnType<typeof cartonFilterPlan>} plan
 * @returns {string}
 */
export function resolveItemFilter(chosen, plan) {
  if (chosen == null) return plan.defaultFilter;
  if (chosen === ItemFilter.OUT_OF_STOCK && plan.oosCount === 0) return ItemFilter.ALL;
  return chosen;
}
